import { useEffect, useState } from "react";
import { http } from "../../lib/api";
import { fmtTL, fmtNum } from "../../lib/format";

const HEAD = [["Personel", "l"], ["Sicil / TC", "l"], ["Şube", "l"], ["Çalışma Gün", "r"], ["Yemek Gün", "r"], ["Brüt Ücret Tutarı", "r"], ["Yemek", "r"], ["Kahvaltı", "r"], ["Yol", "r"], ["Firma Gider Payı", "r"], ["Toplam", "r"]];
const SOURCE = { kart: ["Personel kartı", "text-sky-700"], ucretler: ["Ücretler", "text-slate-500"] };
const cell = "px-3 py-1.5 text-xs";
const num = `${cell} numeric-cell`;

// Yan hak hücresi: tutar + altında "gün × günlük (kaynak)"; gün var ama ücret yoksa YEMEK'te kırmızı uyarı, diğerlerinde sessiz "—" (tanımlı değilse verilmiyor sayılır)
function BenefitCell({ days, daily, source, amount, testid, warn }) {
  if (!days) return <td className={`${num} text-slate-400`}>—</td>;
  if (daily == null) {
    return warn
      ? <td className={num} data-testid={testid}><span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[9px] font-bold uppercase" title="Personel kartında ve Ücretler'de tanım yok">Ücret tanımsız</span><div className="text-[9px] text-slate-400">{days} gün</div></td>
      : <td className={`${num} text-slate-400`} data-testid={testid} title="Personel kartında bu ücret türü tanımlı değil">—</td>;
  }
  const s = SOURCE[source] || ["", ""];
  return (
    <td className={`${num} font-semibold`} data-testid={testid}>
      {fmtTL(amount)}
      <div className={`text-[9px] font-normal ${s[1]}`}>{days} × {fmtTL(daily)} · {s[0]}</div>
    </td>
  );
}

// Brüt ücret hücresi: gün × kart günlüğü; altında "gün × günlük · Aylık/30 | Günlük"; ücret kartı yoksa uyarı
function WageCell({ r, testid }) {
  if (!r.work_days) return <td className={`${num} text-slate-400`}>—</td>;
  if (r.wage_daily == null) return <td className={num} data-testid={testid}><span className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[9px] font-bold uppercase" title="Personel kartında brüt ücret yok">Ücret tanımsız</span><div className="text-[9px] text-slate-400">{r.work_days} gün</div></td>;
  return (
    <td className={`${num} font-semibold`} data-testid={testid}>
      {fmtTL(r.service_amount)}
      <div className="text-[9px] font-normal text-sky-700">{r.work_days} × {fmtTL(r.wage_daily)} · {r.wage_basis === "Aylık" ? "Aylık ÷ 30" : r.wage_basis}</div>
    </td>
  );
}

// Açık hakediş detayında personel bazında kırılım: günler, brüt ücret tutarı (kart), yemek / kahvaltı / yol (gün × personel kartı brütü), toplam
export default function ClaimPersonnelBreakdown({ inv, direction, testid }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!inv.project_id || !inv.period) { setData({ rows: [], totals: {}, people: 0 }); return; }
    http.get("/claims/history/personnel", { params: { project_id: inv.project_id, period: inv.period, direction, ...(inv.category_id ? { category_id: inv.category_id } : {}) } })
      .then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: {}, people: 0 }));
  }, [inv.project_id, inv.period, inv.category_id, direction]);
  if (!data) return <div className="px-3 py-2 text-[11px] text-slate-400 border-t border-slate-100">Personel kırılımı yükleniyor…</div>;
  const t = data.totals || {};
  const kalems = data.kalems || [];
  const kalemTitle = kalems.length
    ? `Kalemler: ${kalems.map((k) => `${k.name} — ${k.basis === "gunluk" ? `Kişi Bazlı ${fmtTL(k.price)} (kişi başı sabit, gün 0 olsa da)` : `Aylık ${fmtTL(k.price)} ÷ 30 = ${fmtTL(k.daily)} × puantaj günü`}`).join(" · ")}`
    : "Bu projede hakediş kalemi (ASIL PERSONEL HAKEDİŞİ altı) tanımlı değil";
  return (
    <div className="border-t-2 border-emerald-200" data-testid={testid}>
      <div className="px-3 py-1.5 bg-emerald-50 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-900">Personel Kırılımı <span className="ml-1 font-normal normal-case text-emerald-700">(puantaj günleri · brüt ücret = gün × kart günlüğü [Günlük aynen, Aylık ÷ 30] · yemek / kahvaltı / yol = gün × personel kartı brütü · firma gider payı = kişi bazlı kalemler kişi başı sabit + aylık kalemler ÷ 30 × gün)</span></span>
        <span className="text-[10px] text-emerald-800" data-testid={`${testid}-summary`}>{data.people} personel · {fmtNum(t.meal_days)} yemek günü · {fmtTL(t.total)}</span>
      </div>
      <div className="overflow-x-auto">
      <table className="w-full tbl">
        <thead><tr className="bg-white">{HEAD.map(([h, a]) => <th key={h} className={`px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold ${a === "r" ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.personnel_id} className="border-t border-slate-100 align-top" data-testid={`${testid}-row-${r.personnel_id}`}>
              <td className={`${cell} font-semibold text-slate-900`}>{r.full_name}</td>
              <td className={`${cell} font-mono text-slate-500`}>{[r.registry_no, r.tc_masked].filter(Boolean).join(" / ") || "—"}</td>
              <td className={cell}>{r.location_name || "—"}</td>
              <td className={num}>{fmtNum(r.work_days)}</td>
              <td className={num}>{fmtNum(r.meal_days)}{r.transport_days !== r.meal_days && <div className="text-[9px] text-slate-400">yol {r.transport_days}</div>}</td>
              <WageCell r={r} testid={`${testid}-wage-${r.personnel_id}`} />
              <BenefitCell days={r.meal_days} daily={r.meal_daily} source={r.meal_source} amount={r.meal_amount} testid={`${testid}-meal-${r.personnel_id}`} warn />
              <BenefitCell days={r.meal_days} daily={r.breakfast_daily} source={r.breakfast_source} amount={r.breakfast_amount} testid={`${testid}-breakfast-${r.personnel_id}`} />
              <BenefitCell days={r.transport_days} daily={r.transport_daily} source={r.transport_source} amount={r.transport_amount} testid={`${testid}-transport-${r.personnel_id}`} />
              <td className={`${num} font-semibold`} data-testid={`${testid}-overhead-${r.personnel_id}`} title={kalemTitle}>
                {kalems.length ? fmtTL(r.overhead_amount) : <span className="text-slate-400">—</span>}
                {kalems.length > 0 && <div className="text-[9px] font-normal text-slate-500">{fmtTL(data.kalem_person)} + {r.work_days} × {fmtTL(data.kalem_daily)}</div>}
              </td>
              <td className={`${num} font-bold text-slate-900`}>{fmtTL(r.total)}</td>
            </tr>
          ))}
          {data.rows.length === 0 && <tr><td colSpan={11} className="px-3 py-2 text-xs text-slate-400">Bu dönem / ana hizmet için puantaj kaydı yok</td></tr>}
        </tbody>
        {data.rows.length > 0 && (
          <tfoot className="bg-emerald-50/60 border-t border-emerald-200">
            <tr data-testid={`${testid}-total`}>
              <td colSpan={3} className="px-3 py-1.5 text-[10px] font-bold text-emerald-900 uppercase tracking-wider">Toplam</td>
              <td className={`${num} font-bold`}>{fmtNum(t.work_days)}</td>
              <td className={`${num} font-bold`}>{fmtNum(t.meal_days)}</td>
              <td className={`${num} font-bold`}>{fmtTL(t.service_amount)}</td>
              <td className={`${num} font-bold`}>{fmtTL(t.meal_amount)}</td>
              <td className={`${num} font-bold`}>{fmtTL(t.breakfast_amount)}</td>
              <td className={`${num} font-bold`}>{fmtTL(t.transport_amount)}</td>
              <td className={`${num} font-bold`} data-testid={`${testid}-total-overhead`}>{fmtTL(t.overhead_amount)}</td>
              <td className={`${num} font-bold text-emerald-900`}>{fmtTL(t.total)}</td>
            </tr>
          </tfoot>
        )}
      </table>
      </div>
    </div>
  );
}
